/**
 * /admin/audit - operator-visible audit log (OCP-52).
 *
 * Read-only. Same filter-sidebar + table layout as
 * AdminWaitlistPage, no actions per row. Fetches
 * `/v1/admin/audit?…` and renders the newest entries at the top
 * (the API already orders DESC).
 *
 * Operator workflow: inspect "what just happened" → filter by
 * action / target type / operator if zooming in.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { AUDIT_TARGET_TYPES, type AuditTargetType } from '@orboto/ocp-shared';

import { api, ApiError } from '../lib/api';
import { formatRelative } from './HomePage';

interface AuditItem {
  id: string;
  operator_email: string;
  /** OCP-699 - who acted: a human operator, a bot account, or the system. */
  actor_kind?: 'human' | 'bot' | 'system';
  action: string;
  target_type: string;
  /** A UUID target (tenant, server, code, account). */
  target_id: string | null;
  /** OCP-1038 - a text target: product slug, provider product id, OMS connection id. */
  target_key?: string | null;
  metadata: unknown;
  ip_address: string | null;
  created_at: string;
}

// OCP-1038 - every target type the api writes (the shared list the REST filter validates against).
const TARGET_TYPE_OPTIONS = AUDIT_TARGET_TYPES;
type TargetTypeOption = AuditTargetType;

interface Filters {
  operator_email: string;
  action: string;
  target_type: TargetTypeOption | '';
  /** OCP-1038 - exact text target key, e.g. a product slug. */
  target_key: string;
  actor_kind: '' | 'human' | 'bot' | 'system';
}

const EMPTY_FILTERS: Filters = { operator_email: '', action: '', target_type: '', target_key: '', actor_kind: '' };

/** OCP-699 - the actor badge: a bot acts under its own grant, the system from a job. */
export function ActorBadge({ kind, displayName }: { kind: 'human' | 'bot' | 'system' | undefined; displayName?: string | null }) {
  if (kind === 'bot') {
    return (
      <span className="ml-1.5 rounded bg-violet-100 dark:bg-violet-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-violet-800 dark:text-violet-300" title={displayName ? `Bot account: ${displayName}` : 'Bot account (acts under its own grant)'} data-testid="actor-bot">
        bot
      </span>
    );
  }
  if (kind === 'system') {
    return <span className="ml-1.5 rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500" title="A scheduled job, no operator">system</span>;
  }
  return null;
}

export function AdminAuditPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<AuditItem[] | null>(null);
  const [actors, setActors] = useState<Record<string, { kind: 'human' | 'bot'; display_name: string | null }>>({});
  const [error, setError] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.operator_email) params.set('operator_email', filters.operator_email);
    if (filters.action) params.set('action', filters.action);
    if (filters.target_type) params.set('target_type', filters.target_type);
    if (filters.target_key) params.set('target_key', filters.target_key);
    if (filters.actor_kind) params.set('actor_kind', filters.actor_kind);
    params.set('limit', '200');
    return `?${params.toString()}`;
  }, [filters]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<{ items: AuditItem[]; actors?: Record<string, { kind: 'human' | 'bot'; display_name: string | null }> }>(`/v1/admin/audit${queryString}`);
      setItems(r.items);
        setActors(r.actors ?? {});
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load audit log (HTTP ${err.status}).`);
      else setError('Could not load audit log.');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <aside className="w-full shrink-0 lg:w-56">
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Filters
            </h2>
            {hasFilters(filters) && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-xs text-brand-500 hover:text-brand-600"
              >
                Reset
              </button>
            )}
          </div>

          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Operator email</span>
            <input
              type="text"
              value={filters.operator_email}
              onChange={(e) =>
                setFilters((f) => ({ ...f, operator_email: e.target.value.trim() }))
              }
              placeholder="exact match"
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>

          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Action</span>
            <input
              type="text"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value.trim() }))}
              placeholder="e.g. waitlist.invite"
              list="audit-action-suggestions"
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
            <datalist id="audit-action-suggestions">
              <option value="waitlist.invite" />
              <option value="waitlist.bulk_invite" />
              <option value="waitlist.decline" />
              <option value="portal_subscription.cancel" />
              <option value="portal_subscription.reactivate" />
              <option value="product.updated" />
              <option value="product.pushed" />
            </datalist>
          </label>

          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Target type</span>
            <select
              value={filters.target_type}
              onChange={(e) =>
                setFilters((f) => ({ ...f, target_type: e.target.value as TargetTypeOption | '' }))
              }
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All</option>
              {TARGET_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Target key</span>
            <input
              type="text"
              value={filters.target_key}
              onChange={(e) => setFilters((f) => ({ ...f, target_key: e.target.value.trim() }))}
              placeholder="e.g. orboto-saas-team"
              aria-label="target key"
              title="A target that is not a UUID: product slug, provider product id, OMS connection id (exact match)"
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Actor</span>
            <select
              value={filters.actor_kind}
              onChange={(e) => setFilters((f) => ({ ...f, actor_kind: e.target.value as Filters['actor_kind'] }))}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              aria-label="actor kind"
            >
              <option value="">All</option>
              <option value="human">Humans</option>
              <option value="bot">Bots</option>
              <option value="system">System</option>
            </select>
          </label>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {items === null
              ? 'Loading…'
              : `${items.length} ${items.length === 1 ? 'event' : 'events'} (newest first)`}
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 flex items-center justify-between rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <AuditTable items={items} actors={actors} />
      </main>
    </div>
  );
}

function hasFilters(f: Filters): boolean {
  return Boolean(f.operator_email || f.action || f.target_type || f.target_key || f.actor_kind);
}

function AuditTable({ items, actors = {} }: { items: AuditItem[] | null; actors?: Record<string, { kind: 'human' | 'bot'; display_name: string | null }> }) {
  // OCP-599 - per-row metadata expander: the before/after, reason and
  // source an override or money movement recorded are in `metadata`; an
  // operator reviewing an agent-driven write needs them without the api.
  const [openId, setOpenId] = useState<string | null>(null);
  if (items === null) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No audit events match the current filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">When</th>
            <th className="px-3 py-2">Operator</th>
            <th className="px-3 py-2">Action</th>
            <th className="px-3 py-2">Target</th>
            <th className="px-3 py-2">Target id / key</th>
            <th className="px-3 py-2">Details</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((it) => {
            const meta = it.metadata as Record<string, unknown> | null;
            const source = meta && typeof meta.source === 'string' ? meta.source : null;
            const reason = meta && typeof meta.reason === 'string' ? meta.reason : null;
            const hasMeta = meta != null && typeof meta === 'object' && Object.keys(meta).length > 0;
            const open = openId === it.id;
            return (
              <Fragment key={it.id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">
                    {formatRelative(it.created_at)}
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                    {it.operator_email}
                    <ActorBadge kind={it.actor_kind} displayName={actors[it.operator_email]?.display_name} />
                  </td>
                  <td className="px-3 py-2">
                    <code className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-300">
                      {it.action}
                    </code>
                    {source && (
                      <span
                        className="ml-1.5 rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500"
                        title="Where the write came from"
                      >
                        {source}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.target_type}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500" title={it.target_id ?? it.target_key ?? undefined}>
                    {it.target_key ? (
                      <span data-testid="audit-target-key">{it.target_key}</span>
                    ) : it.target_id ? (
                      it.target_id.slice(0, 8) + '…'
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {hasMeta ? (
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setOpenId(open ? null : it.id)}
                        className="text-brand-600 hover:underline dark:text-brand-400"
                      >
                        {open ? 'Hide' : reason ? 'Reason + details' : 'Details'}
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
                {open && hasMeta && (
                  <tr className="bg-gray-50 dark:bg-gray-800/40">
                    <td colSpan={6} className="px-3 py-2">
                      {reason && (
                        <p className="mb-1 text-xs text-gray-700 dark:text-gray-300">
                          <span className="font-medium">Reason:</span> {reason}
                        </p>
                      )}
                      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-white dark:bg-gray-900 p-2 font-mono text-[11px] text-gray-700 dark:text-gray-300">
                        {JSON.stringify(meta, null, 2)}
                      </pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
